import type {
  BrandMenuFull,
  BrandMenuGet,
  BrandMenuParsedDish,
  BrandMenuParseResponse,
  BrandMenuPost,
  MenuCategoryCreatePost,
  MenuCategoryGet,
  MenuCategoryPatch,
  MenuDictItem,
  MenuDishCreatePost,
  MenuDishFullPost,
  MenuDishGet,
  MenuDishPatch,
  MenuDishPost,
  MenuMediaItemGet,
  MenuMediaItemPost,
  MenuReorderItem,
} from "@/api/admin/brands/types";
import { RESPONSE_STATUSES } from "@/api/constants";
import type { ApiError } from "@/types/api";
import { formatPrice } from "@/utils/formatPrice";

import type { BrandFormMenuType, CategoryType, DishFormType } from "./types";
import { DEFAULT_PRICE_DECIMALS } from "./types";

const TASTE_KEYS = [
  ["Spice", "spiceLevel"],
  ["Sweetness", "sweetnessLevel"],
  ["Sourness", "sournessLevel"],
  ["Bitterness", "bitternessLevel"],
  ["Umami", "umamiLevel"],
] as const;

export const createDefaultMenuPost = (): BrandMenuPost => ({
  categories: [],
  isActive: false,
});

export const isNotFoundError = (error: unknown): boolean =>
  (error as ApiError)?.status === RESPONSE_STATUSES.NOT_FOUND;

const toOptionalString = (value?: number | null): string =>
  value == null ? "" : String(value);

const toOptionalNumber = (value?: string): number | undefined => {
  if (value == null || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const dictItemsToIdArray = (items?: MenuDictItem[]): string[] =>
  (items ?? []).map(item => item.id).filter(id => id.trim().length > 0);

const toFormIdArray = (ids?: string[]): string[] =>
  (ids ?? []).filter(id => id.trim().length > 0);

/** Backend 0-4 → UI 1-5 (0 on UI = unset, shown with no switches lit) */
const tasteLevelFromApi = (value?: number | null): number =>
  value == null ? 0 : value + 1;

/** UI 0 = unset (omit from API), UI 1-5 → backend 0-4 */
const tasteLevelToApi = (uiValue: number): number | undefined =>
  uiValue <= 0 ? undefined : uiValue - 1;

export const getBranchScopeLabelFromUnavailable = (
  unavailableBranchIds: string[] | undefined,
  totalBranches: number
): string => {
  if (totalBranches === 0) return "No branches";
  const unavailableCount = unavailableBranchIds?.length ?? 0;
  if (unavailableCount === 0) return "All branches";
  if (unavailableCount >= totalBranches) return "Not available";
  const availableCount = totalBranches - unavailableCount;
  return `${availableCount} branch${availableCount > 1 ? "es" : ""}`;
};

export const toUnavailableBranchIds = (
  branchAvailability: Array<{ branchId: string; available: boolean }>
): string[] =>
  branchAvailability.filter(b => !b.available).map(b => b.branchId);

const sortMenuMediaByOrder = (
  items: MenuMediaItemGet[] = []
): MenuMediaItemGet[] => [...items].sort((a, b) => a.order - b.order);

/**
 * Turns a dish from the API into form values.
 *
 * `decimals` comes from the menu's currency. It matters because JSON cannot
 * carry a trailing zero — a price stored as `2.750` arrives as the number
 * `2.75`, and `String(2.75)` would put `"2.75"` in the edit field. Padding it
 * back is the only way a Jordanian price shows the third digit it was saved
 * with.
 */
export const mapDishGetToForm = (
  dish: MenuDishGet,
  decimals: number = DEFAULT_PRICE_DECIMALS
): DishFormType => {
  const sortedMedia = sortMenuMediaByOrder(dish.mediaItems);

  return {
    id: dish.id,
    dishNameEnglish: dish.nameEn,
    dishNameArabic: dish.nameAr,
    descriptionEnglish: dish.descriptionEn ?? "",
    descriptionArabic: dish.descriptionAr ?? "",
    price: formatPrice(dish.price, decimals),
    preparationTime: toOptionalString(dish.preparationTime),
    active: dish.active ?? true,
    unavailableBranchIds: dish.unavailableBranchIds ?? [],
    flagsAndStatuses: dictItemsToIdArray(dish.flagsAndStatuses),
    meal: dictItemsToIdArray(dish.mealTimes),
    occasion: dictItemsToIdArray(dish.occasions),
    ratings: {
      Spice: tasteLevelFromApi(dish.spiceLevel),
      Sweetness: tasteLevelFromApi(dish.sweetnessLevel),
      Sourness: tasteLevelFromApi(dish.sournessLevel),
      Bitterness: tasteLevelFromApi(dish.bitternessLevel),
      Umami: tasteLevelFromApi(dish.umamiLevel),
    },
    dietTags: dictItemsToIdArray(dish.diets),
    allergenTags: dictItemsToIdArray(dish.allergens),
    calories: toOptionalString(dish.calories),
    burnCaloriesTime: toOptionalString(dish.walkingMinutesToBurn),
    protein: toOptionalString(dish.protein),
    fiber: toOptionalString(dish.fiber),
    totalFat: toOptionalString(dish.totalFat),
    totalSugar: toOptionalString(dish.totalSugars),
    saturatedFat: toOptionalString(dish.saturatedFat),
    addedSugars: toOptionalString(dish.addedSugars),
    transFat: toOptionalString(dish.transFat),
    salt: toOptionalString(dish.salt),
    cholesterol: toOptionalString(dish.cholesterol),
    sodium: toOptionalString(dish.sodium),
    carbohydrate: toOptionalString(dish.carbohydrate),
    caffeine: toOptionalString(dish.caffeine),
    saltContent: dish.isHighSalt ?? false,
    mediaItems: sortedMedia.map(item => ({
      mediaId: "",
      processedMediaId: item.id,
    })),
    images: {
      media: sortedMedia.map(item => ({
        id: item.id,
        type: item.type,
        order: item.order,
        variants: item.variants.map(variant => ({
          url: variant.url,
          type: variant.type,
        })),
        mediaId: item.mediaId,
      })),
    },
  };
};

export const mapCategoryFormToCreatePost = (
  nameEn: string,
  nameAr: string | undefined,
  order: number
): MenuCategoryCreatePost => ({
  nameEn,
  nameAr: nameAr ?? "",
  order,
});

export const findCreatedCategoryInMenu = (
  menu: BrandMenuGet,
  payload: MenuCategoryCreatePost
): MenuCategoryGet | undefined =>
  menu.categories?.find(
    category =>
      (category.category?.nameEn ?? category.nameEn) === payload.nameEn &&
      category.order === payload.order
  );

export const mapCategoryFormToPatch = (
  nameEn: string,
  nameAr: string | undefined,
  order: number
): MenuCategoryPatch => ({
  nameEn,
  nameAr: nameAr ?? "",
  order,
});

export const mapCategoryGetToForm = (
  category: MenuCategoryGet,
  decimals: number = DEFAULT_PRICE_DECIMALS
): CategoryType => ({
  id: category.id,
  categoryDictId: category.category?.id,
  categoryNameEn: category.category?.nameEn ?? category.nameEn ?? "",
  categoryNameAr: category.category?.nameAr ?? category.nameAr ?? "",
  order: category.order ?? 0,
  dishes: (category.dishes ?? []).map(dish => mapDishGetToForm(dish, decimals)),
});

const mapDishTasteLevels = (dish: DishFormType) => {
  const ratings = dish.ratings ?? {
    Spice: 0,
    Sweetness: 0,
    Sourness: 0,
    Bitterness: 0,
    Umami: 0,
  };

  return TASTE_KEYS.reduce<Record<string, number | undefined>>(
    (acc, [formKey, apiKey]) => {
      acc[apiKey] = tasteLevelToApi(ratings[formKey] ?? 0);
      return acc;
    },
    {}
  );
};

const mapDishNutritionFields = (dish: DishFormType) => ({
  calories: toOptionalNumber(dish.calories),
  walkingMinutesToBurn: toOptionalNumber(dish.burnCaloriesTime),
  protein: toOptionalNumber(dish.protein),
  totalFat: toOptionalNumber(dish.totalFat),
  saturatedFat: toOptionalNumber(dish.saturatedFat),
  transFat: toOptionalNumber(dish.transFat),
  cholesterol: toOptionalNumber(dish.cholesterol),
  carbohydrate: toOptionalNumber(dish.carbohydrate),
  fiber: toOptionalNumber(dish.fiber),
  totalSugars: toOptionalNumber(dish.totalSugar),
  addedSugars: toOptionalNumber(dish.addedSugars),
  salt: toOptionalNumber(dish.salt),
  sodium: toOptionalNumber(dish.sodium),
  caffeine: toOptionalNumber(dish.caffeine),
  isHighSalt: dish.saltContent ?? false,
});

const mapDishMediaItemsForCreate = (dish: DishFormType): string[] =>
  (dish.mediaItems ?? [])
    .map(item => item.mediaId || item.processedMediaId || "")
    .filter(id => id.trim().length > 0);

const mapDishMediaItemsForPatch = (dish: DishFormType): MenuMediaItemPost[] =>
  (dish.mediaItems ?? []).map(item => ({
    mediaId: item.mediaId ?? "",
    processedMediaId: item.processedMediaId ?? "",
  }));

export const mapDishFormToCreatePost = (
  dish: DishFormType,
  displayOrder: number,
  unavailableBranchIds: string[]
): MenuDishCreatePost => {
  const tasteLevels = mapDishTasteLevels(dish);

  return {
    nameEn: dish.dishNameEnglish,
    nameAr: dish.dishNameArabic,
    descriptionEn: dish.descriptionEnglish || undefined,
    descriptionAr: dish.descriptionArabic || undefined,
    price: Number(dish.price),
    preparationTime: toOptionalNumber(dish.preparationTime),
    spiceLevel: tasteLevels.spiceLevel,
    sweetnessLevel: tasteLevels.sweetnessLevel,
    sournessLevel: tasteLevels.sournessLevel,
    bitternessLevel: tasteLevels.bitternessLevel,
    umamiLevel: tasteLevels.umamiLevel,
    mealTimeIds: toFormIdArray(dish.meal),
    occasionIds: toFormIdArray(dish.occasion),
    dietIds: toFormIdArray(dish.dietTags),
    allergenIds: toFormIdArray(dish.allergenTags),
    flagsAndStatusIds: toFormIdArray(dish.flagsAndStatuses),
    ...mapDishNutritionFields(dish),
    displayOrder,
    unavailableBranchIds,
    mediaItems: mapDishMediaItemsForCreate(dish),
    active: dish.active ?? true,
  };
};

export const mapDishFormToUpdatePatch = (
  dish: DishFormType,
  displayOrder: number,
  unavailableBranchIds: string[],
  menuCategoryId?: string
): MenuDishPatch => {
  const tasteLevels = mapDishTasteLevels(dish);

  return {
    nameEn: dish.dishNameEnglish,
    nameAr: dish.dishNameArabic,
    descriptionEn: dish.descriptionEnglish || undefined,
    descriptionAr: dish.descriptionArabic || undefined,
    price: Number(dish.price),
    preparationTime: toOptionalNumber(dish.preparationTime),
    spiceLevel: tasteLevels.spiceLevel,
    sweetnessLevel: tasteLevels.sweetnessLevel,
    sournessLevel: tasteLevels.sournessLevel,
    bitternessLevel: tasteLevels.bitternessLevel,
    umamiLevel: tasteLevels.umamiLevel,
    mealTimeIds: toFormIdArray(dish.meal),
    occasionIds: toFormIdArray(dish.occasion),
    dietIds: toFormIdArray(dish.dietTags),
    allergenIds: toFormIdArray(dish.allergenTags),
    flagsAndStatusIds: toFormIdArray(dish.flagsAndStatuses),
    ...mapDishNutritionFields(dish),
    displayOrder,
    menuCategoryId,
    unavailableBranchIds,
    mediaItems: mapDishMediaItemsForPatch(dish),
    active: dish.active,
  };
};

export const mapCategoryIdsToReorderItems = (
  ids: string[]
): MenuReorderItem[] => ids.map((id, order) => ({ id, order }));

const mapDishFormToPost = (
  dish: DishFormType,
  displayOrder: number
): MenuDishPost => {
  const tasteLevels = mapDishTasteLevels(dish);

  return {
    nameEn: dish.dishNameEnglish,
    nameAr: dish.dishNameArabic,
    descriptionEn: dish.descriptionEnglish || undefined,
    descriptionAr: dish.descriptionArabic || undefined,
    price: Number(dish.price),
    preparationTime: toOptionalNumber(dish.preparationTime),
    spiceLevel: tasteLevels.spiceLevel,
    sweetnessLevel: tasteLevels.sweetnessLevel,
    sournessLevel: tasteLevels.sournessLevel,
    bitternessLevel: tasteLevels.bitternessLevel,
    umamiLevel: tasteLevels.umamiLevel,
    mealTimeIds: toFormIdArray(dish.meal),
    occasionIds: toFormIdArray(dish.occasion),
    dietIds: toFormIdArray(dish.dietTags),
    allergenIds: toFormIdArray(dish.allergenTags),
    flagsAndStatusIds: toFormIdArray(dish.flagsAndStatuses),
    calories: toOptionalNumber(dish.calories),
    walkingMinutesToBurn: toOptionalNumber(dish.burnCaloriesTime),
    protein: toOptionalNumber(dish.protein),
    totalFat: toOptionalNumber(dish.totalFat),
    saturatedFat: toOptionalNumber(dish.saturatedFat),
    transFat: toOptionalNumber(dish.transFat),
    cholesterol: toOptionalNumber(dish.cholesterol),
    carbohydrate: toOptionalNumber(dish.carbohydrate),
    fiber: toOptionalNumber(dish.fiber),
    totalSugars: toOptionalNumber(dish.totalSugar),
    addedSugars: toOptionalNumber(dish.addedSugars),
    salt: toOptionalNumber(dish.salt),
    sodium: toOptionalNumber(dish.sodium),
    caffeine: toOptionalNumber(dish.caffeine),
    isHighSalt: dish.saltContent ?? false,
    displayOrder,
    unavailableBranchIds: dish.unavailableBranchIds ?? [],
    mediaItems: mapDishMediaItemsForPatch(dish),
    active: dish.active ?? true,
  };
};

const PARSED_DISH_ARABIC_PLACEHOLDER = "غير محدد";

const mapParsedDishToForm = (
  dish: BrandMenuParsedDish,
  categoryIndex: number,
  dishIndex: number,
  decimals: number = DEFAULT_PRICE_DECIMALS
): DishFormType => ({
  id: `parsed-dish-${categoryIndex}-${dishIndex}`,
  dishNameEnglish: dish.nameEn,
  dishNameArabic: dish.nameAr?.trim() || PARSED_DISH_ARABIC_PLACEHOLDER,
  descriptionEnglish: "",
  descriptionArabic: "",
  price: formatPrice(dish.price, decimals),
  preparationTime:
    dish.preparationTime != null ? String(dish.preparationTime) : "",
  active: true,
  flagsAndStatuses: [],
  meal: [],
  occasion: [],
  ratings: {
    Spice: 0,
    Sweetness: 0,
    Sourness: 0,
    Bitterness: 0,
    Umami: 0,
  },
  dietTags: [],
  allergenTags: [],
});

export const isEmptyMenuParseResponse = (
  response: BrandMenuParseResponse
): boolean => {
  const entries = Object.entries(response);
  if (entries.length === 0) return true;
  return entries.every(([, dishes]) => dishes.length === 0);
};

// The parse response is a plain {category: dishes} map with no currency in it,
// so unlike mapMenuGetToForm the decimals have to come from the caller.
export const mapMenuParseResponseToCategories = (
  response: BrandMenuParseResponse,
  decimals: number = DEFAULT_PRICE_DECIMALS
): CategoryType[] =>
  Object.entries(response).map(([categoryName, dishes], categoryIndex) => ({
    id: `parsed-category-${categoryIndex}`,
    categoryNameEn: categoryName,
    categoryNameAr: "",
    order: categoryIndex,
    dishes: dishes.map((dish, dishIndex) =>
      mapParsedDishToForm(dish, categoryIndex, dishIndex, decimals)
    ),
  }));

export const mapMenuGetToForm = (
  menu: BrandMenuGet
): Partial<BrandFormMenuType> => {
  // The menu response carries the brand's currency, so the decimals are read
  // from the same object being mapped. No caller has to remember to pass them,
  // and there is no window where the currency has not arrived yet.
  const decimals = menu.currency?.decimals ?? DEFAULT_PRICE_DECIMALS;

  return {
    menuId: menu.id,
    categories: (menu.categories ?? []).map(category =>
      mapCategoryGetToForm(category, decimals)
    ),
    isActive: menu.isActive ?? false,
    updatedAt: menu.updatedAt,
  };
};

export const mapFormToMenuPost = (menu: BrandFormMenuType): BrandMenuPost => {
  const categories = (menu.categories ?? []).map((category, index) => ({
    order: category.order ?? index,
    category: {
      id: category.categoryDictId ?? category.id ?? `category-${index}`,
      type: "category",
      nameEn: category.categoryNameEn,
      nameAr: category.categoryNameAr ?? "",
    } satisfies MenuDictItem,
    dishes: category.dishes.map((dish, dishIndex) =>
      mapDishFormToPost(dish, dishIndex)
    ),
  }));

  return {
    categories,
    isActive: menu.isActive ?? false,
  };
};

export const mapParsedCategoriesToFullMenu = (
  existingMenu: BrandMenuGet,
  parsedCategories: CategoryType[]
): BrandMenuFull => {
  const baseOrder = existingMenu.categories?.length ?? 0;

  return {
    categories: parsedCategories.map((category, index) => ({
      nameEn: category.categoryNameEn,
      nameAr: category.categoryNameAr ?? "",
      order: baseOrder + index,
      dishes: category.dishes.map(mapDishFormToFullMenuDish),
    })),
  };
};

const mapDishFormToFullMenuDish = (dish: DishFormType): MenuDishFullPost => {
  const preparationTime = toOptionalNumber(dish.preparationTime);

  return {
    nameEn: dish.dishNameEnglish,
    nameAr: dish.dishNameArabic,
    price: Number(dish.price),
    ...(preparationTime !== undefined ? { preparationTime } : {}),
  };
};

export const isMenuReadyToSave = (menu: BrandFormMenuType): boolean => {
  const cuisines = menu.cuisine ?? [];
  const categories = menu.categories ?? [];
  const hasDish = categories.some(
    category => (category.dishes?.length ?? 0) > 0
  );

  return cuisines.length >= 1 && categories.length >= 1 && hasDish;
};

export const getDefaultFormMenu = (): BrandFormMenuType => ({
  cuisine: [],
  categories: [],
  isActive: false,
});
